// packages/carbon/src/no-code/pulse/runtime.ts
//
// The zero-overhead gate. This file is the ONLY pulse module the store-creation path imports, and it
// pulls in nothing heavy: the `TapModule` contract below is a hand-written structural type (the real
// tap implementation lives in the platform app, which is the only place that statically imports it),
// so importing this file does NOT bundle any tap/floor/shallowCap code into the runner.
//
// Contract:
//  - `isPulseEnabled()` is decided once, at app entry, BEFORE any store is created.
//      · builder → the platform app registers an enabler (setPulseEnabler) at its root; InterfaceAppProviders
//        calls requestPulseEnable() for non-runner modes, which runs that enabler (setTaps + setPulseEnabled).
//      · runner  → no enabler is ever registered, so requestPulseEnable() is a no-op and nothing wires.
//        Future runner opt-in would lazy-register an enabler behind a sessionStorage flag.
//  - When disabled, `getTaps()` returns undefined, so createGlobalStateStore uses the RAW setters directly:
//    no wrapper frame, no per-call check, zero cost on the hot state-write path.

import type { ScopeId, StoreKey } from './types';

// Structural contract for the tap module the platform builder wires in via setTaps(). It mirrors the
// signatures of apps/platform/.../pulse/tapState.ts; carbon can't import that module (it lives in the
// app, above carbon in the dependency graph), so the two must stay in sync by hand.
interface TapModule {
  tapMapSetter: <V>(
    scopeId: ScopeId,
    map: StoreKey['map'],
    getState: () => object,
    mapField: string,
    rawSetter: (id: string, value: V) => void,
    instIdOf?: (id: string) => string | undefined,
  ) => (id: string, value: V) => void;
  tapUpdater: <F extends (id: string, mutator: never) => void>(
    scopeId: ScopeId,
    map: StoreKey['map'],
    getState: () => object,
    mapField: string,
    rawUpdater: F,
    instIdOf?: (id: string) => string | undefined,
  ) => F;
  tapMapRemover: (
    scopeId: ScopeId,
    map: StoreKey['map'],
    getState: () => object,
    mapField: string,
    rawRemover: (id: string) => void,
    instIdOf?: (id: string) => string | undefined,
  ) => (id: string) => void;
  tapMultiSetter: <V>(
    scopeId: ScopeId,
    map: StoreKey['map'],
    getState: () => object,
    mapField: string,
    rawMultiSetter: (values: Partial<Record<string, V>>) => void,
    instIdOf?: (id: string) => string | undefined,
  ) => (values: Partial<Record<string, V>>) => void;
  tapMapMultiRemover: (
    scopeId: ScopeId,
    map: StoreKey['map'],
    getState: () => object,
    mapField: string,
    rawRemoveMultiple: (ids: string[]) => void,
    instIdOf?: (id: string) => string | undefined,
  ) => (ids: string[]) => void;
}

let pulseEnabled = false;
let taps: TapModule | undefined;
let enabler: (() => void) | undefined;

export function isPulseEnabled(): boolean {
  return pulseEnabled;
}

/** Called once at app entry (builder statically; runner behind the sessionStorage flag). */
export function setPulseEnabled(enabled: boolean): void {
  pulseEnabled = enabled;
}

/** Wires the lazily/statically loaded tap functions. Until this runs, getTaps() is undefined. */
export function setTaps(module: TapModule): void {
  taps = module;
}

/** The tap functions when pulse is enabled, else undefined → callers use the raw setters. */
export function getTaps(): TapModule | undefined {
  return taps;
}

/**
 * Registered once by the host app that owns the pulse devtools (the platform builder).
 * Runner bundles never register one, so requestPulseEnable() stays a no-op there and
 * none of the tap/buffer code has to exist in their bundles at all.
 */
export function setPulseEnabler(fn: () => void): void {
  enabler = fn;
}

/** Called by InterfaceAppProviders for non-runner modes, before any store is created. */
export function requestPulseEnable(): void {
  enabler?.();
}

export const __resetForTest = (): void => {
  pulseEnabled = false;
  taps = undefined;
  enabler = undefined;
};
